/* eslint-disable lingui/no-unlocalized-strings */
import './types';
import { Dictionary, isNil } from 'lodash';
import {
  getSessionTokenData,
  saveSessionTokenInStorage,
} from '../services/auth';
import { ValidationError } from '../formalist';
import { SITE } from '../../site.config';
import * as types from './types';
import { ClaimsData } from 'interfaces/healthcare';
import {
  Account,
  BenefitItemType,
  BenefitUses,
  ProtectionItem,
} from 'interfaces/benefits';

export type ApiOptions = {
  includeAuthorization?: boolean;
  throwError?: boolean;
  // Return null on 404 instead of throwing an error
  nullOn404?: boolean;
  // Add a special header for automated tests that increments each time an endpoint is called.
  // This might be needed to properly record and replay a sequence of API calls that
  // are identical in request, but the response varies depending on other user state.
  incrementTestHeader?: boolean;
};

export class ApiError extends Error {
  status: number;
  type: string;
  fields: { [n: string]: string };

  constructor(status: number, error: types.ApiErrorResponse, ...params: any[]) {
    // pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.name = 'ApiError';
    this.message = error?.message || `Network Error: ${status}`;

    // custom error information
    this.status = status;
    this.type = error.type;
    this.fields = error.fields;
  }
}

const onSessionDataSetEventName = 'savviApi-sessionDataSet';

export class Api {
  sessionData: any;
  onSessionDataSet: Event;
  testHeaderIndex: { [endpoint: string]: number };

  constructor() {
    this.onSessionDataSet = new Event(onSessionDataSetEventName);
    this.testHeaderIndex = {};
    getSessionTokenData()
      .then((sessionData) => this.setSession(sessionData))
      .catch(() => undefined);
  }

  setSession = (sessionData: any) => {
    this.sessionData = sessionData;
    window.dispatchEvent(this.onSessionDataSet);
  };

  addOnSessionDataSetListener = (handler: (e: Event) => void) => {
    return window.addEventListener(onSessionDataSetEventName, handler);
  };

  removeOnSessionDataSetListener = (handler: (e: Event) => void) => {
    return window.removeEventListener(onSessionDataSetEventName, handler);
  };

  getSession = () => {
    return this.sessionData;
  };

  injectSession = async (sessionData: any) => {
    saveSessionTokenInStorage(sessionData);
    this.setSession(sessionData);
  };

  request = async (
    method: string,
    endpoint: string,
    data: any = undefined,
    {
      includeAuthorization = true,
      throwError = true,
      // Return null on 404 instead of throwing an error
      nullOn404 = false,
      // Add a special header for automated tests that increments each time an endpoint is called.
      // This might be needed to properly record and replay a sequence of API calls that
      // are identical in request, but the response varies depending on other user state.
      incrementTestHeader = false,
    }: ApiOptions = {},
  ): Promise<any> => {
    const headers: { [name: string]: string } = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (includeAuthorization) {
      const sessionToken = this.sessionData?.session_token;
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
    }

    // @ts-ignore
    if (incrementTestHeader && window.AUTOMATED_TEST_ENV) {
      if (isNil(this.testHeaderIndex[endpoint])) {
        this.testHeaderIndex[endpoint] = 0;
      }
      headers['X-Test-Id'] = String(++this.testHeaderIndex[endpoint]);
    }

    const options: { [name: string]: any } = { method, headers };

    if (data && method != 'GET') {
      options['body'] = JSON.stringify(data);
    }

    const url = !endpoint.startsWith('http')
      ? `${SITE.API_URL}/${endpoint}`
      : endpoint;

    const response = await fetch(url, options);

    const responseData = await response.json();

    // Unauthorized attempt
    if (response.status === 401) {
      // There is a race condition where this puts us in an infinite
      // redirect loop when the api request happens before auth has succeeded
      //window.location.assign(`/${RouteNames.Welcome}`);
      return;
    }

    if (response.status === 404 && nullOn404) {
      return null;
    }

    if (response.status != 200 && throwError) {
      if (responseData?.error.type == 'SchemaValidationError') {
        // throw a special error so our form library can treat it as an
        // async validation error
        throw new ValidationError(
          responseData?.error?.message,
          responseData?.error?.fields,
        );
      } else {
        // eslint-disable-next-line no-console
        console.log(responseData);

        throw new ApiError(response.status, responseData?.error);
      }
    }

    return responseData;
  };

  get = async (
    endpoint: string,
    {
      includeAuthorization = true,
      throwError = true,
      nullOn404 = false,
      incrementTestHeader = false,
    }: ApiOptions = {},
  ): Promise<any> => {
    return await this.request('GET', endpoint, undefined, {
      includeAuthorization,
      throwError,
      nullOn404,
      incrementTestHeader,
    });
  };

  post = async (
    endpoint: string,
    data: any = {},
    {
      includeAuthorization = true,
      throwError = true,
      nullOn404 = false,
      incrementTestHeader = false,
    }: ApiOptions = {},
  ): Promise<any> => {
    return await this.request('POST', endpoint, data, {
      includeAuthorization,
      throwError,
      nullOn404,
      incrementTestHeader,
    });
  };

  put = async (
    endpoint: string,
    data: any = {},
    {
      includeAuthorization = true,
      throwError = true,
      nullOn404 = false,
      incrementTestHeader = false,
    }: ApiOptions = {},
  ): Promise<any> => {
    return await this.request('PUT', endpoint, data, {
      includeAuthorization,
      throwError,
      nullOn404,
      incrementTestHeader,
    });
  };

  patch = async (
    endpoint: string,
    data: any = undefined,
    {
      includeAuthorization = true,
      throwError = true,
      nullOn404 = false,
      incrementTestHeader = false,
    }: ApiOptions = {},
  ): Promise<any> => {
    return await this.request('PATCH', endpoint, data, {
      includeAuthorization,
      throwError,
      nullOn404,
      incrementTestHeader,
    });
  };

  delete = async (
    endpoint: string,
    {
      includeAuthorization = true,
      throwError = true,
      nullOn404 = false,
      incrementTestHeader = false,
    }: ApiOptions = {},
  ): Promise<any> => {
    return await this.request('DELETE', endpoint, undefined, {
      includeAuthorization,
      throwError,
      nullOn404,
      incrementTestHeader,
    });
  };

  signupUser = async ({
    email,
    username,
    invite_code,
  }: {
    email?: string;
    username?: string;
    invite_code: string;
  }): Promise<types.User> => {
    const signupData: {
      app_id: string;
      client_id: string;
      invite_code: string;
      email?: string;
      username?: string;
    } = {
      app_id: SITE.APP_ID,
      client_id: SITE.CLIENT_ID,
      invite_code: invite_code,
    };
    if (username) {
      signupData['username'] = username;
    } else {
      signupData['email'] = email;
    }

    const stepData = await this.post('auth/signup', signupData, {
      includeAuthorization: false,
    });

    return await this.finalizeSignin(stepData);
  };

  signupUserWithInviteCode = async (
    invite_code: string,
  ): Promise<types.User> => {
    const invite = await this.getInvite(invite_code);
    return await this.signupUser({
      email: invite.metadata?.email as string | undefined,
      username: invite.metadata?.username as string | undefined,
      invite_code,
    });
  };

  signinUserWithToken = async ({
    signin_token,
  }: {
    signin_token: string;
  }): Promise<types.User> => {
    const signinData: {
      app_id: string;
      client_id: string;
      type: string;
      token: string;
    } = {
      app_id: SITE.APP_ID,
      client_id: SITE.CLIENT_ID,
      type: 'token',
      token: signin_token,
    };

    const stepData = await this.post('auth/signin', signinData, {
      includeAuthorization: false,
    });

    return await this.finalizeSignin(stepData);
  };

  finalizeSignin = async (stepData: types.NextStep) => {
    if (!stepData.next_url) {
      throw Error('could not complete sign-in');
    }
    const finalizeData = await this.post(
      stepData.next_url,
      {},
      { includeAuthorization: false, throwError: false },
    );

    const sessionData = {
      session_token: finalizeData.session_token,
      expires_at: finalizeData.expires_at,
      renew_by: finalizeData.renew_by,
    };

    // set session in OG data architecture
    saveSessionTokenInStorage(sessionData);

    // set session in new react-query api
    this.setSession(sessionData);

    return finalizeData.user;
  };

  getInvite = async (invite_code: string): Promise<types.Invite> => {
    return this.get(`auth/invites/${invite_code}`);
  };

  getUser = async (): Promise<types.User> => {
    return this.get('user');
  };

  createSigninToken = async (
    last_location?: string | null,
  ): Promise<string> => {
    if (last_location !== undefined) {
      await this.updateHousehold({ last_location });
    }
    return (await this.post('user/signin-token'))?.token;
  };

  getData = async (): Promise<any> => {
    return this.get('selectsmart/data');
  };

  getEmployer = async (): Promise<types.EmployerBenefitsYear> => {
    return this.get('selectsmart/employer?plan_year=active');
  };

  getHousehold = async (): Promise<types.Household> => {
    return this.get('selectsmart/household');
  };

  updateHousehold = async (
    household: types.HouseholdUpdate,
  ): Promise<types.Household> => {
    return this.patch(`selectsmart/household`, household);
  };

  getPeople = async (): Promise<types.People> => {
    return this.get('selectsmart/people');
  };

  getPerson = async (person_id: string): Promise<types.Person> => {
    return this.get(`selectsmart/people/${person_id}`);
  };

  updatePerson = async (
    personId: string,
    person: types.PersonUpdate,
  ): Promise<types.Person> => {
    return this.patch(`selectsmart/people/${personId}`, person);
  };

  createPerson = async (person: types.NewPerson): Promise<types.Person> => {
    return this.post(`selectsmart/people`, person);
  };

  getIncomes = async (): Promise<{ [k: string]: types.Income }> => {
    return this.get('selectsmart/incomes');
  };

  updateIncome = async (
    personId: string,
    income: types.IncomeUpdate,
  ): Promise<types.Income> => {
    return this.patch(`selectsmart/incomes/${personId}`, income);
  };

  getEmergencyFundGoal = async (): Promise<types.EmergencyFundGoal> => {
    return this.get('selectsmart/goals/emergencyfund');
  };

  updateEmergencyFundGoal = async (
    data: Omit<types.EmergencyFundGoalUpdate, 'type'>,
  ): Promise<types.EmergencyFundGoal> => {
    return this.patch('selectsmart/goals/emergencyfund', {
      type: 'emergencyfund',
      ...data,
    });
  };

  getRetirementGoal = async (): Promise<types.RetirementGoal> => {
    return this.get('selectsmart/goals/retirement');
  };

  updateIntegrations = async (): Promise<types.OperationStatus> => {
    return this.post('/selectsmart/integrations');
  };

  getCurrentElections = async (): Promise<types.PlanBenefit[]> => {
    return this.get(
      'selectsmart/planbenefits?plan_year=current&status=enrolled',
    );
  };

  getPlans = async (): Promise<Dictionary<types.PlanBenefit>> => {
    return this.get('selectsmart/plans');
  };

  getAccounts = async (): Promise<Dictionary<Account>> => {
    return this.get('selectsmart/accounts');
  };

  patchAccounts = async (): Promise<Dictionary<Account>> => {
    return this.patch(`selectsmart/accounts`, {});
  };

  getTypicalServices = async (): Promise<any> => {
    return this.get('selectsmart/assumptions/typical-services');
  };

  submitBundleToBenefitfocusIntegration = async (
    bundlesId: string,
    bundleKey: string,
  ): Promise<void> => {
    return this.post(
      `selectsmart/integrations/benefitfocus/submit-bundle/${bundlesId}/${bundleKey}`,
      null,
    );
  };

  getClaimsSummary = async (): Promise<ClaimsData> => {
    return this.get(`selectsmart/claims/summary`);
  };

  refreshClaimsSummary = async (): Promise<ClaimsData> => {
    return this.post(`selectsmart/claims/summary`, null);
  };

  updateClaimsOptIn = async (optIn: boolean | null): Promise<void> => {
    return this.patch(`selectsmart/claims/optin`, {
      has_optin: optIn,
    });
  };

  getBundlesSummary = async (
    bundlesId: string,
    bundleKey: string,
  ): Promise<any> => {
    return this.get(
      `selectsmart/public/bundles-summary/${bundlesId}/${bundleKey}`,
      {
        includeAuthorization: false,
      },
    );
  };

  getBenefitTypes = async (): Promise<BenefitItemType[]> => {
    return this.get('selectsmart/benefit-types', { incrementTestHeader: true });
  };

  getLdiData = async (): Promise<Dictionary<ProtectionItem>> => {
    return this.get('selectsmart/ldi/data');
  };

  getBenefitUses = async (): Promise<Dictionary<BenefitUses>> => {
    return this.get('selectsmart/benefit-uses');
  };
}

// create default api singleton we will use except in tests where
// we might want to stub/mock the backend requests
export const api = new Api();

// inject into window for testing (e.g. Playwright)
if (window) {
  window.api = api;
}
